<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import CustomReportsAPI from 'dashboard/api/omni/customReports';
import SlotsConfigAPI from 'dashboard/api/omni/slotsConfig';
import subDays from 'date-fns/subDays';
import startOfYear from 'date-fns/startOfYear';
import { getUnixStartOfDay, getUnixEndOfDay } from 'shared/helpers/DateHelper';
import Modal from 'dashboard/components/Modal.vue';
import WootDateRangePicker from 'dashboard/components/ui/DateRangePicker.vue';
import ReportHeader from './components/ReportHeader.vue';
import BarChart from 'shared/components/charts/BarChart.vue';
import LineChart from 'shared/components/charts/LineChart.vue';
import PieChart from 'shared/components/charts/PieChart.vue';
import { useSectionPermissions } from 'dashboard/composables/useSectionPermissions';

const { t } = useI18n();

const { canEditSection } = useSectionPermissions();

const PALETTE = [
  '#1f93ff',
  '#28c76f',
  '#ff9f43',
  '#ea5455',
  '#7367f0',
  '#00cfe8',
  '#9c8cfc',
  '#f8c291',
];

const bots = ref([]);
const metadata = ref({ fields: [], slots: [] });
const savedReports = ref([]);
const result = ref(null);
const previewResult = ref(null);
const isRunning = ref(false);

const showModal = ref(false);
const activeReportId = ref(null);

const reportName = ref('');
const editingId = ref(null);
const selectedBotId = ref(null);

const source = ref('fields');
const fieldName = ref('');
const dimension = ref('field_value');
const metric = ref('count');
const granularity = ref('day');
const chartType = ref('bar');
const filters = ref([]);

// View-time period filter (lives in the main view, not the modal). Default "all".
const PRESET_PERIODS = ['all', 'last_7', 'last_30', 'last_90', 'year'];
const viewPeriod = ref('all');
const viewFrom = ref(null);
const viewTo = ref(null);
const customDateValue = ref([]);

const computePeriodRange = period => {
  const now = new Date();
  switch (period) {
    case 'last_7':
      return { from: getUnixStartOfDay(subDays(now, 6)), to: getUnixEndOfDay(now) };
    case 'last_30':
      return { from: getUnixStartOfDay(subDays(now, 29)), to: getUnixEndOfDay(now) };
    case 'last_90':
      return { from: getUnixStartOfDay(subDays(now, 89)), to: getUnixEndOfDay(now) };
    case 'year':
      return { from: getUnixStartOfDay(startOfYear(now)), to: getUnixEndOfDay(now) };
    default:
      return { from: null, to: null };
  }
};

const dimensionOptions = computed(() => {
  if (source.value === 'fields') return ['field_value', 'time'];
  if (source.value === 'slots') return ['slot_name', 'slot_status', 'time'];
  return ['conversation_status', 'time'];
});

const metricOptions = computed(() => {
  if (source.value === 'slots') return ['count', 'avg_time_to_conclude'];
  return ['count'];
});

const needsField = computed(
  () => source.value === 'fields' || dimension.value === 'field_value'
);
const isTimeDimension = computed(() => dimension.value === 'time');

const sourceLabel = key => t(`CUSTOM_REPORTS.SOURCES.${key.toUpperCase()}`);
const dimensionLabel = key =>
  t(`CUSTOM_REPORTS.DIMENSIONS.${key.toUpperCase()}`);
const metricLabel = key => t(`CUSTOM_REPORTS.METRICS.${key.toUpperCase()}`);

const chartUnit = computed(() =>
  metric.value === 'avg_time_to_conclude'
    ? t('CUSTOM_REPORTS.UNITS.SECONDS')
    : t('CUSTOM_REPORTS.UNITS.COUNT')
);

const makeCollection = res => {
  if (!res?.series?.length) return { labels: [], datasets: [] };
  const labels = res.series.map(s => s.label);
  const data = res.series.map(s => s.value);
  if (chartType.value === 'pie') {
    return {
      labels,
      datasets: [
        {
          data,
          backgroundColor: labels.map((_, i) => PALETTE[i % PALETTE.length]),
        },
      ],
    };
  }
  return {
    labels,
    datasets: [
      {
        label: metricLabel(metric.value),
        data,
        backgroundColor: '#1f93ff',
        borderColor: '#1f93ff',
        fill: false,
        tension: 0.3,
      },
    ],
  };
};

const chartCollection = computed(() => makeCollection(result.value));
const previewCollection = computed(() => makeCollection(previewResult.value));

const chartOptions = computed(() => {
  const unit = chartUnit.value;
  if (chartType.value === 'pie') {
    return {
      plugins: {
        tooltip: {
          callbacks: {
            label: ctx => ` ${ctx.label}: ${ctx.parsed} ${unit}`,
          },
        },
      },
    };
  }
  return {
    plugins: {
      tooltip: {
        callbacks: {
          label: ctx => ` ${ctx.parsed.y} ${unit}`,
        },
      },
    },
  };
});

const fillRatePercent = computed(() => {
  if (result.value?.totals?.fill_rate === undefined) return null;
  return Math.round(result.value.totals.fill_rate * 100);
});

const fetchBots = async () => {
  const { data } = await SlotsConfigAPI.getBots();
  bots.value = data || [];
  if (!selectedBotId.value && bots.value.length) {
    selectedBotId.value = bots.value[0].id;
  }
};

const fetchMetadata = async () => {
  if (!selectedBotId.value) return;
  const { data } = await CustomReportsAPI.getMetadata(selectedBotId.value);
  metadata.value = data;
  if (!fieldName.value && data.fields?.length) {
    fieldName.value = data.fields[0];
  }
};

const fetchSavedReports = async () => {
  const { data } = await CustomReportsAPI.list();
  savedReports.value = data || [];
};

const buildQueryConfig = (useViewRange = true) => ({
  source: source.value,
  field_name: needsField.value ? fieldName.value : null,
  dimension: dimension.value,
  granularity: granularity.value,
  metric: metric.value,
  chart_type: chartType.value,
  filters: filters.value.filter(f => f.field_name || f.value),
  // Saved reports default to "all" (no date filter); the date range is applied
  // at view-time from the main-view period selector.
  date_range: useViewRange
    ? { since: viewFrom.value, until: viewTo.value }
    : { since: null, until: null },
});

const runQuery = async () => {
  if (!selectedBotId.value) return null;
  if (needsField.value && !fieldName.value) {
    useAlert(t('CUSTOM_REPORTS.FIELD_REQUIRED'));
    return null;
  }
  isRunning.value = true;
  try {
    const { data } = await CustomReportsAPI.run(
      selectedBotId.value,
      buildQueryConfig()
    );
    return data;
  } catch (error) {
    useAlert(error?.response?.data?.error || t('CUSTOM_REPORTS.RUN_ERROR'));
    return null;
  } finally {
    isRunning.value = false;
  }
};

// Preview inside the modal (does not touch the main view).
const previewReport = async () => {
  const data = await runQuery();
  if (data) previewResult.value = data;
};

const loadReportIntoForm = async report => {
  editingId.value = report.id;
  reportName.value = report.name;
  selectedBotId.value = report.agent_bot_id;
  const cfg = report.query_config || {};
  source.value = cfg.source || 'fields';
  fieldName.value = cfg.field_name || '';
  dimension.value = cfg.dimension || 'field_value';
  metric.value = cfg.metric || 'count';
  granularity.value = cfg.granularity || 'day';
  chartType.value = cfg.chart_type || 'bar';
  filters.value = cfg.filters || [];
  await fetchMetadata();
};

// Open a saved report in the main view (no modal). Resets the view period to "all".
const selectReport = async report => {
  await loadReportIntoForm(report);
  activeReportId.value = report.id;
  viewPeriod.value = 'all';
  viewFrom.value = null;
  viewTo.value = null;
  customDateValue.value = [];
  result.value = await runQuery();
};

// View-time period change for the currently selected report (preset buttons).
const applyViewPeriod = async period => {
  viewPeriod.value = period;
  const { from, to } = computePeriodRange(period);
  viewFrom.value = from;
  viewTo.value = to;
  if (activeReportId.value) result.value = await runQuery();
};

// Called when the user confirms a custom date range via the date picker.
const onCustomDateChange = async ([startDate, endDate]) => {
  if (!startDate || !endDate) return;
  viewPeriod.value = 'custom';
  viewFrom.value = getUnixStartOfDay(startDate);
  viewTo.value = getUnixEndOfDay(endDate);
  customDateValue.value = [startDate, endDate];
  if (activeReportId.value) result.value = await runQuery();
};

const openCustomPicker = () => {
  viewPeriod.value = 'custom';
};

const openEdit = async report => {
  await loadReportIntoForm(report);
  viewPeriod.value = 'all';
  viewFrom.value = null;
  viewTo.value = null;
  customDateValue.value = [];
  previewResult.value = null;
  showModal.value = true;
};

const openNew = () => {
  editingId.value = null;
  reportName.value = '';
  source.value = 'fields';
  fieldName.value = metadata.value.fields?.[0] || '';
  dimension.value = 'field_value';
  metric.value = 'count';
  granularity.value = 'day';
  chartType.value = 'bar';
  filters.value = [];
  viewPeriod.value = 'all';
  viewFrom.value = null;
  viewTo.value = null;
  customDateValue.value = [];
  previewResult.value = null;
  showModal.value = true;
};

const closeModal = () => {
  showModal.value = false;
};

const saveReport = async () => {
  if (!reportName.value.trim()) {
    useAlert(t('CUSTOM_REPORTS.NAME_REQUIRED'));
    return;
  }
  if (needsField.value && !fieldName.value) {
    useAlert(t('CUSTOM_REPORTS.FIELD_REQUIRED'));
    return;
  }
  const payload = {
    name: reportName.value.trim(),
    agent_bot_id: selectedBotId.value,
    query_config: buildQueryConfig(false),
  };
  try {
    if (editingId.value) {
      await CustomReportsAPI.update(editingId.value, payload);
    } else {
      const { data } = await CustomReportsAPI.create(payload);
      editingId.value = data.id;
    }
    useAlert(t('CUSTOM_REPORTS.SAVE_SUCCESS'));
    await fetchSavedReports();
    activeReportId.value = editingId.value;
    result.value = await runQuery();
    closeModal();
  } catch (error) {
    useAlert(
      error?.response?.data?.errors?.[0] || t('CUSTOM_REPORTS.SAVE_ERROR')
    );
  }
};

const deleteReport = async report => {
  try {
    await CustomReportsAPI.delete(report.id);
    await fetchSavedReports();
    if (activeReportId.value === report.id) {
      result.value = null;
      activeReportId.value = null;
      if (savedReports.value.length) {
        await selectReport(savedReports.value[0]);
      }
    }
  } catch (error) {
    useAlert(t('CUSTOM_REPORTS.DELETE_ERROR'));
  }
};

const addFilter = () => {
  filters.value.push({
    source: 'field',
    field_name: metadata.value.fields?.[0] || '',
    operator: 'equals',
    value: '',
  });
};

const removeFilter = index => {
  filters.value.splice(index, 1);
};

watch(source, () => {
  if (!dimensionOptions.value.includes(dimension.value)) {
    dimension.value = dimensionOptions.value[0];
  }
  if (!metricOptions.value.includes(metric.value)) {
    metric.value = metricOptions.value[0];
  }
});

const onBotChange = async () => {
  fieldName.value = '';
  await fetchMetadata();
};

onMounted(async () => {
  await fetchBots();
  await Promise.all([fetchMetadata(), fetchSavedReports()]);
  if (savedReports.value.length) {
    await selectReport(savedReports.value[0]);
  }
});
</script>

<template>
  <ReportHeader
    :header-title="$t('CUSTOM_REPORTS.HEADER')"
    :header-description="$t('CUSTOM_REPORTS.DESCRIPTION')"
  >
    <button
      v-if="canEditSection('custom_reports')"
      class="px-3 py-2 text-sm font-medium text-white rounded-lg bg-n-brand"
      @click="openNew"
    >
      {{ $t('CUSTOM_REPORTS.NEW') }}
    </button>
  </ReportHeader>

  <div class="flex flex-col gap-4 lg:flex-row">
    <!-- Saved reports library -->
    <aside
      class="flex-shrink-0 w-full lg:w-64 p-4 bg-white dark:bg-slate-900 rounded-lg border border-slate-75 dark:border-slate-800"
    >
      <h2 class="mb-3 text-lg font-semibold text-slate-900 dark:text-slate-25">
        {{ $t('CUSTOM_REPORTS.SAVED') }}
      </h2>
      <p
        v-if="!savedReports.length"
        class="text-sm text-slate-500 dark:text-slate-400"
      >
        {{ $t('CUSTOM_REPORTS.EMPTY') }}
      </p>
      <ul class="flex flex-col gap-1">
        <li
          v-for="report in savedReports"
          :key="report.id"
          class="flex items-center justify-between px-3 py-2 rounded-lg transition-colors group cursor-pointer"
          :class="
            activeReportId === report.id
              ? 'bg-woot-50 dark:bg-woot-900/20 text-woot-600'
              : 'hover:bg-slate-50 dark:hover:bg-slate-800'
          "
        >
          <button
            class="flex-1 text-sm font-medium text-left truncate"
            @click="selectReport(report)"
          >
            {{ report.name }}
          </button>
          <div
            v-if="canEditSection('custom_reports')"
            class="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <button
              class="p-1 rounded text-slate-500 hover:text-woot-600 hover:bg-woot-50 dark:hover:bg-woot-900/30"
              :title="$t('CUSTOM_REPORTS.EDIT')"
              @click="openEdit(report)"
            >
              <span class="i-lucide-pen size-3.5" />
            </button>
            <button
              class="p-1 rounded text-slate-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30"
              @click="deleteReport(report)"
            >
              <span class="i-lucide-trash-2 size-3.5" />
            </button>
          </div>
        </li>
      </ul>
    </aside>

    <!-- Main preview -->
    <div class="flex flex-col flex-1 gap-4 min-w-0">
      <div
        v-if="result"
        class="p-4 bg-white dark:bg-slate-900 rounded-lg border border-slate-75 dark:border-slate-800"
      >
        <div
          class="flex flex-col gap-3 pb-4 mb-4 border-b border-slate-75 dark:border-slate-800"
        >
          <div class="flex flex-wrap items-center justify-between gap-2">
            <h2
              v-if="reportName"
              class="text-lg font-semibold text-slate-900 dark:text-slate-25"
            >
              {{ reportName }}
            </h2>
            <!-- Period pill buttons -->
            <div class="flex flex-wrap items-center gap-1.5">
              <button
                v-for="p in PRESET_PERIODS"
                :key="p"
                class="px-3 py-1.5 text-sm font-medium rounded-md transition-colors"
                :class="
                  viewPeriod === p
                    ? 'text-woot-600 bg-woot-50 dark:bg-woot-900/20'
                    : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
                "
                @click="applyViewPeriod(p)"
              >
                {{ $t(`CUSTOM_REPORTS.PERIODS.${p.toUpperCase()}`) }}
              </button>
              <!-- Custom date range pill -->
              <button
                class="px-3 py-1.5 text-sm font-medium rounded-md transition-colors"
                :class="
                  viewPeriod === 'custom'
                    ? 'text-woot-600 bg-woot-50 dark:bg-woot-900/20'
                    : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
                "
                @click="openCustomPicker"
              >
                {{ $t('CUSTOM_REPORTS.PERIODS.CUSTOM') }}
              </button>
            </div>
          </div>
          <!-- Custom date picker — visible only when "Personalizado" is active -->
          <div v-if="viewPeriod === 'custom'" class="flex items-center gap-2">
            <span class="text-sm text-slate-500 dark:text-slate-400">
              {{ $t('CUSTOM_REPORTS.PERIODS.CUSTOM_LABEL') }}
            </span>
            <WootDateRangePicker
              :value="customDateValue"
              :confirm-text="$t('CUSTOM_REPORTS.PERIODS.CONFIRM')"
              :placeholder="$t('CUSTOM_REPORTS.PERIODS.PLACEHOLDER')"
              @change="onCustomDateChange"
            />
          </div>
        </div>

        <div class="flex flex-wrap gap-6 mb-4">
          <div>
            <p class="text-xs uppercase text-slate-500 dark:text-slate-400">
              {{ $t('CUSTOM_REPORTS.TOTAL_CONVERSATIONS') }}
            </p>
            <p class="text-xl font-semibold text-slate-900 dark:text-slate-25">
              {{ result.totals.conversations }}
            </p>
          </div>
          <div v-if="fillRatePercent !== null">
            <p class="text-xs uppercase text-slate-500 dark:text-slate-400">
              {{ $t('CUSTOM_REPORTS.FILL_RATE') }}
            </p>
            <p class="text-xl font-semibold text-slate-900 dark:text-slate-25">
              {{ fillRatePercent }}%
            </p>
          </div>
        </div>

        <p
          v-if="!result.series.length"
          class="py-8 text-sm text-center text-slate-500 dark:text-slate-400"
        >
          {{ $t('CUSTOM_REPORTS.NO_DATA') }}
        </p>

        <template v-else>
          <!-- Chart -->
          <div v-if="chartType !== 'table'" class="h-80 mb-6">
            <BarChart
              v-if="chartType === 'bar'"
              :collection="chartCollection"
              :chart-options="chartOptions"
            />
            <LineChart
              v-else-if="chartType === 'line'"
              :collection="chartCollection"
              :chart-options="chartOptions"
            />
            <PieChart
              v-else-if="chartType === 'pie'"
              :collection="chartCollection"
              :chart-options="chartOptions"
            />
          </div>

          <!-- Results table (always shown below the chart) -->
          <div class="overflow-x-auto">
            <h3
              class="mb-2 text-sm font-semibold text-slate-700 dark:text-slate-200"
            >
              {{ $t('CUSTOM_REPORTS.TABLE_TITLE') }}
            </h3>
            <table class="w-full text-sm">
              <thead>
                <tr
                  class="border-b border-slate-75 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50"
                >
                  <th
                    v-for="col in result.table.columns"
                    :key="col"
                    class="px-3 py-2 font-medium text-left text-slate-600 dark:text-slate-300"
                  >
                    {{ col }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(row, index) in result.table.rows"
                  :key="index"
                  class="border-b border-slate-75 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50"
                >
                  <td
                    v-for="(cell, cellIndex) in row"
                    :key="cellIndex"
                    class="px-3 py-2 text-slate-700 dark:text-slate-200"
                  >
                    {{ cell }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </template>
      </div>

      <div
        v-else
        class="p-8 text-sm text-center bg-white dark:bg-slate-900 rounded-lg border border-slate-75 dark:border-slate-800 text-slate-500 dark:text-slate-400"
      >
        {{ $t('CUSTOM_REPORTS.EMPTY') }}
      </div>
    </div>
  </div>

  <!-- Builder modal -->
  <Modal v-model:show="showModal" :on-close="closeModal" size="medium">
    <div class="flex flex-col gap-4 p-8">
      <h2 class="text-lg font-medium text-n-slate-12">
        {{ editingId ? $t('CUSTOM_REPORTS.EDIT') : $t('CUSTOM_REPORTS.NEW') }}
      </h2>

      <input
        v-model="reportName"
        type="text"
        class="px-3 py-2 text-sm rounded-md border border-n-weak bg-n-alpha-black2 text-n-slate-12"
        :placeholder="$t('CUSTOM_REPORTS.NAME_PLACEHOLDER')"
      />

      <div class="grid grid-cols-1 gap-3 md:grid-cols-2">
        <label class="flex flex-col gap-1 text-sm">
          <span class="text-n-slate-11">{{ $t('CUSTOM_REPORTS.BOT') }}</span>
          <select
            v-model="selectedBotId"
            class="px-3 py-2 text-sm rounded-md border border-n-weak bg-n-alpha-black2 text-n-slate-12"
            @change="onBotChange"
          >
            <option v-for="bot in bots" :key="bot.id" :value="bot.id">
              {{ bot.name }}
            </option>
          </select>
        </label>

        <label class="flex flex-col gap-1 text-sm">
          <span class="text-n-slate-11">{{ $t('CUSTOM_REPORTS.SOURCE') }}</span>
          <select
            v-model="source"
            class="px-3 py-2 text-sm rounded-md border border-n-weak bg-n-alpha-black2 text-n-slate-12"
          >
            <option value="fields">{{ sourceLabel('fields') }}</option>
            <option value="slots">{{ sourceLabel('slots') }}</option>
            <option value="conversations">
              {{ sourceLabel('conversations') }}
            </option>
          </select>
        </label>

        <label v-if="needsField" class="flex flex-col gap-1 text-sm">
          <span class="text-n-slate-11">{{ $t('CUSTOM_REPORTS.FIELD') }}</span>
          <select
            v-if="metadata.fields && metadata.fields.length"
            v-model="fieldName"
            class="px-3 py-2 text-sm rounded-md border border-n-weak bg-n-alpha-black2 text-n-slate-12"
          >
            <option v-for="field in metadata.fields" :key="field" :value="field">
              {{ field }}
            </option>
          </select>
          <span v-else class="text-xs text-n-ruby-11">
            {{ $t('CUSTOM_REPORTS.NO_FIELDS') }}
          </span>
        </label>

        <label class="flex flex-col gap-1 text-sm">
          <span class="text-n-slate-11">
            {{ $t('CUSTOM_REPORTS.DIMENSION') }}
          </span>
          <select
            v-model="dimension"
            class="px-3 py-2 text-sm rounded-md border border-n-weak bg-n-alpha-black2 text-n-slate-12"
          >
            <option v-for="dim in dimensionOptions" :key="dim" :value="dim">
              {{ dimensionLabel(dim) }}
            </option>
          </select>
        </label>

        <label class="flex flex-col gap-1 text-sm">
          <span class="text-n-slate-11">{{ $t('CUSTOM_REPORTS.METRIC') }}</span>
          <select
            v-model="metric"
            class="px-3 py-2 text-sm rounded-md border border-n-weak bg-n-alpha-black2 text-n-slate-12"
          >
            <option v-for="m in metricOptions" :key="m" :value="m">
              {{ metricLabel(m) }}
            </option>
          </select>
        </label>

        <label v-if="isTimeDimension" class="flex flex-col gap-1 text-sm">
          <span class="text-n-slate-11">
            {{ $t('CUSTOM_REPORTS.GRANULARITY') }}
          </span>
          <select
            v-model="granularity"
            class="px-3 py-2 text-sm rounded-md border border-n-weak bg-n-alpha-black2 text-n-slate-12"
          >
            <option value="day">
              {{ $t('CUSTOM_REPORTS.GRANULARITIES.DAY') }}
            </option>
            <option value="week">
              {{ $t('CUSTOM_REPORTS.GRANULARITIES.WEEK') }}
            </option>
            <option value="month">
              {{ $t('CUSTOM_REPORTS.GRANULARITIES.MONTH') }}
            </option>
          </select>
        </label>

        <label class="flex flex-col gap-1 text-sm">
          <span class="text-n-slate-11">
            {{ $t('CUSTOM_REPORTS.CHART_TYPE') }}
          </span>
          <select
            v-model="chartType"
            class="px-3 py-2 text-sm rounded-md border border-n-weak bg-n-alpha-black2 text-n-slate-12"
          >
            <option value="bar">{{ $t('CUSTOM_REPORTS.CHART_TYPES.BAR') }}</option>
            <option value="line">
              {{ $t('CUSTOM_REPORTS.CHART_TYPES.LINE') }}
            </option>
            <option value="pie">{{ $t('CUSTOM_REPORTS.CHART_TYPES.PIE') }}</option>
            <option value="table">
              {{ $t('CUSTOM_REPORTS.CHART_TYPES.TABLE') }}
            </option>
          </select>
        </label>
      </div>

      <!-- Filters -->
      <div>
        <div class="flex items-center justify-between mb-2">
          <span class="text-sm font-medium text-n-slate-12">
            {{ $t('CUSTOM_REPORTS.FILTERS') }}
          </span>
          <button class="text-sm text-n-brand" @click="addFilter">
            + {{ $t('CUSTOM_REPORTS.ADD_FILTER') }}
          </button>
        </div>
        <div
          v-for="(filter, index) in filters"
          :key="index"
          class="flex flex-wrap items-center gap-2 mb-2"
        >
          <select
            v-model="filter.field_name"
            class="px-2 py-1.5 text-sm rounded-md border border-n-weak bg-n-alpha-black2 text-n-slate-12"
          >
            <option v-for="field in metadata.fields" :key="field" :value="field">
              {{ field }}
            </option>
          </select>
          <select
            v-model="filter.operator"
            class="px-2 py-1.5 text-sm rounded-md border border-n-weak bg-n-alpha-black2 text-n-slate-12"
          >
            <option value="equals">
              {{ $t('CUSTOM_REPORTS.OPERATORS.EQUALS') }}
            </option>
            <option value="contains">
              {{ $t('CUSTOM_REPORTS.OPERATORS.CONTAINS') }}
            </option>
            <option value="exists">
              {{ $t('CUSTOM_REPORTS.OPERATORS.EXISTS') }}
            </option>
          </select>
          <input
            v-if="filter.operator !== 'exists'"
            v-model="filter.value"
            type="text"
            class="px-2 py-1.5 text-sm rounded-md border border-n-weak bg-n-alpha-black2 text-n-slate-12"
            :placeholder="$t('CUSTOM_REPORTS.VALUE')"
          />
          <button class="text-n-ruby-9" @click="removeFilter(index)">
            <span class="i-lucide-x size-4" />
          </button>
        </div>
      </div>

      <div class="flex items-center justify-end gap-2 pt-2">
        <button
          class="px-3 py-2 text-sm font-medium rounded-lg border border-n-weak text-n-slate-12"
          :disabled="isRunning"
          @click="previewReport"
        >
          {{ $t('CUSTOM_REPORTS.PREVIEW') }}
        </button>
        <button
          class="px-3 py-2 text-sm font-medium rounded-lg border border-n-weak text-n-slate-12"
          @click="closeModal"
        >
          {{ $t('CUSTOM_REPORTS.CANCEL') }}
        </button>
        <button
          class="px-3 py-2 text-sm font-medium text-white rounded-lg bg-n-brand"
          @click="saveReport"
        >
          {{ $t('CUSTOM_REPORTS.SAVE') }}
        </button>
      </div>

      <!-- In-modal preview -->
      <div
        v-if="previewResult"
        class="pt-3 mt-1 border-t border-n-weak"
      >
        <p
          v-if="!previewResult.series.length"
          class="py-6 text-sm text-center text-n-slate-11"
        >
          {{ $t('CUSTOM_REPORTS.NO_DATA') }}
        </p>
        <div v-else-if="chartType !== 'table'" class="h-64">
          <BarChart
            v-if="chartType === 'bar'"
            :collection="previewCollection"
            :chart-options="chartOptions"
          />
          <LineChart
            v-else-if="chartType === 'line'"
            :collection="previewCollection"
            :chart-options="chartOptions"
          />
          <PieChart
            v-else-if="chartType === 'pie'"
            :collection="previewCollection"
            :chart-options="chartOptions"
          />
        </div>
        <table v-else class="w-full text-sm">
          <thead>
            <tr class="border-b border-n-weak">
              <th
                v-for="col in previewResult.table.columns"
                :key="col"
                class="px-2 py-2 font-medium text-left text-n-slate-11"
              >
                {{ col }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(row, index) in previewResult.table.rows"
              :key="index"
              class="border-b border-n-weak"
            >
              <td
                v-for="(cell, cellIndex) in row"
                :key="cellIndex"
                class="px-2 py-2 text-n-slate-12"
              >
                {{ cell }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </Modal>
</template>
